/**
 * The settings panels, as data — the same treatment the module sections get.
 *
 * S-D3 drew eight. Four are built, and the other four are listed with the
 * session that brings them, because every one of those four needs a backend
 * that does not exist yet:
 *
 *  * **Connections** — GSC (S-B22), LinkedIn (S-B17), sender identity (S-B18),
 *    publish targets (S-B28). There is nothing to connect to.
 *  * **Brand voice** — consumed by content generation, S-B25.
 *  * **Budget caps** — the per-tenant budget ledger is S-B7.
 *  * ~~**Notifications**~~ — **built in S-B10.** The concern S-B8 recorded here
 *    was that a preferences table keyed on events no code emits would be a
 *    schema written against a guess; the answer was to take the vocabulary from
 *    S-D3's own table — five approved rows — and record, per event, which
 *    session emits it. Three are emitted today and the rest render with a
 *    "from S-Bxx" note, the same treatment the sidebar gives an unshipped
 *    module. The panel also carries the webhook endpoints and their delivery
 *    log, because S-D3 designed no panel for those and adding a ninth entry to
 *    approved navigation would be inventing information architecture.
 *  * ~~**Danger zone**~~ — **built in S-B12.** DPDP export and organisation
 *    deletion are §8 launch requirements that session owns, and both are live.
 *    Pause-all-automation is still there and still disabled, because there is
 *    genuinely no automation to pause until the SEO and outreach runners land —
 *    rendered rather than removed, so the approved panel keeps its shape.
 *
 * Shown disabled rather than hidden, exactly as the sidebar sections are, so
 * the settings information architecture is visible and none of it is a stub.
 */

export interface SettingsPanel {
  label: string
  to?: string
  /** Permission required to *see* it — mirrors the route dependency on the API. */
  permission?: string
  arrivesIn: string
}

export const SETTINGS_PANELS: readonly SettingsPanel[] = [
  {
    label: 'Organisation',
    to: '/settings/organisation',
    permission: 'tenant:read',
    arrivesIn: 'S-B8',
  },
  { label: 'Team & roles', to: '/settings/team', permission: 'members:read', arrivesIn: 'S-B8' },
  { label: 'Connections', arrivesIn: 'S-B17' },
  { label: 'Brand voice', arrivesIn: 'S-B25' },
  { label: 'Budget caps', arrivesIn: 'S-B7' },
  {
    label: 'Notifications',
    to: '/settings/notifications',
    permission: 'tenant:read',
    arrivesIn: 'S-B10',
  },
  { label: 'Active sessions', to: '/settings/sessions', arrivesIn: 'S-B8' },
  {
    label: 'Danger zone',
    to: '/settings/danger-zone',
    permission: 'data:export',
    arrivesIn: 'S-B12',
  },
]

export function panelForPath(pathname: string): SettingsPanel | undefined {
  return SETTINGS_PANELS.find((panel) => panel.to === pathname)
}
